import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import { useFabFilters } from '@/components/product-insights/use-fab-data';
import {
  PiDeniedPage,
  PiError,
  PiFilterChips,
  PiPageLayout,
  PiSearch,
  PiToolbar,
} from '@/components/product-insights/pi-kit';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { IcrOutcomes, IcrStats } from '@/components/product-insights/icr/IcrOverview';
import { IcrUniversityTable } from '@/components/product-insights/icr/IcrUniversityTable';
import {
  ICR_STATUSES,
  ICR_STATUS_LABEL,
  DEFAULT_ICR_STATUS,
  parseIcrStatus,
  type IcrRequestParams,
  type IcrStatus,
} from '@/components/product-insights/icr/icr-model';
import { useIcrMonths, useIcrSummary } from '@/components/product-insights/icr/use-icr-data';
import { SegmentedControl, Select } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout, type LayoutBlockDefault } from '@/lib/use-page-layout';

/**
 * /product-insights/icr — ICR (In-Country Representation).
 *
 * The product specialist's question: what has each university's in-country
 * representative delivered? A live read of the upstream ICR summary through
 * EMERGE's `/product-insights/icr` proxy (nothing stored). Figures are the
 * reps' own monthly reports.
 *
 * Layout: the shared Product Insights template (PiPageLayout → PiToolbar →
 * headline tiles + outcome funnel → university table). No tabs: one view.
 *
 * - URL state: `cycle` (absent = SEED FAB's current cycle, the same
 *   `d_business_cycles` list the SEED FAB page uses), `status` (absent =
 *   published), `q` (search, client-side, debounced 250 ms into the URL).
 *   Each non-default value is a removable chip; Reset clears all three.
 * - The summary is fetched once the cycle catalogue has settled, so the request
 *   always names a cycle the picker can show.
 * - Month-by-month figures are fetched per row, only when a row is expanded.
 * - No `product-insights.read` grant: refusal panel, and nothing is fetched.
 * - Layout manager (2026-10-20, `product-insights.icr`): the tiles stay fixed;
 *   Outcomes and Universities are movable blocks, both full width by default.
 */

const SEARCH_PARAM = 'q';

/** Block ids are stored per person: never rename one. */
const ICR_LAYOUT: readonly LayoutBlockDefault[] = [
  { id: 'outcomes', label: 'Outcomes' },
  { id: 'universities', label: 'Universities' },
];
const SEARCH_DEBOUNCE_MS = 250;

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

function parseCycle(raw: string | null): number | null {
  if (raw === null) return null;
  const id = Number.parseInt(raw, 10);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function ProductInsightsIcrPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead('product-insights');
  if (!canRead) return <PiDeniedPage product="icr" title="In-Country Representation" />;
  return <IcrContent />;
}

function IcrContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const layout = usePageLayout('product-insights.icr', ICR_LAYOUT);
  const urlCycleId = parseCycle(searchParams.get('cycle'));
  const status = parseIcrStatus(searchParams.get('status'));
  const urlSearch = searchParams.get(SEARCH_PARAM) ?? '';

  /* --- debounced search ------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState(urlSearch);
  const [seenUrlSearch, setSeenUrlSearch] = useState(urlSearch);
  if (seenUrlSearch !== urlSearch) {
    setSeenUrlSearch(urlSearch);
    setDraftSearch(urlSearch);
  }

  useEffect(() => {
    if (draftSearch === urlSearch) return;
    const timer = window.setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (draftSearch.trim() === '') next.delete(SEARCH_PARAM);
          else next.set(SEARCH_PARAM, draftSearch);
          return next;
        },
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draftSearch, urlSearch, setSearchParams]);

  /* --- cycles ------------------------------------------------------------ */

  const filtersResult = useFabFilters(urlCycleId);
  const { filters } = filtersResult;
  const reloadFilters = filtersResult.reload;
  const filtersSettled = filters !== null || filtersResult.error !== null;

  const selectedCycleId = urlCycleId ?? filters?.resolvedCycleId ?? null;
  const selectedCycle = useMemo(
    () => filters?.cycles.find((c) => c.id === selectedCycleId) ?? null,
    [filters, selectedCycleId],
  );

  const writeParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onCycleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const id = Number.parseInt(event.target.value, 10);
      if (!Number.isSafeInteger(id)) return;
      // The resolved (current) cycle is the default: an absent key.
      writeParam('cycle', filters && id === filters.resolvedCycleId ? null : String(id));
    },
    [filters, writeParam],
  );

  const onStatusChange = useCallback(
    (next: IcrStatus) => writeParam('status', next === DEFAULT_ICR_STATUS ? null : next),
    [writeParam],
  );

  const resetFilters = useCallback(() => {
    setDraftSearch('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('cycle');
        next.delete('status');
        next.delete(SEARCH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  /* --- data ------------------------------------------------------------- */

  const params = useMemo<IcrRequestParams | null>(() => {
    if (!filtersSettled && urlCycleId === null) return null;
    return {
      ...(selectedCycleId !== null ? { cycleId: selectedCycleId } : {}),
      status,
      groupBy: 'university',
    };
  }, [filtersSettled, urlCycleId, selectedCycleId, status]);

  const [reloadToken, setReloadToken] = useState(0);
  const summary = useIcrSummary(params, reloadToken);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const paramsKey = params === null ? '' : JSON.stringify(params);
  const [seenParamsKey, setSeenParamsKey] = useState(paramsKey);
  if (seenParamsKey !== paramsKey) {
    // A new cycle or status is a different set of rows: close every expansion.
    setSeenParamsKey(paramsKey);
    setExpanded(new Set());
  }
  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const monthBase = useMemo<IcrRequestParams | null>(() => {
    if (params === null) return null;
    const { groupBy: _groupBy, ...rest } = params;
    return rest;
  }, [params]);
  const months = useIcrMonths(monthBase, summary.data?.rows ?? [], expanded, reloadToken);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const retry = useCallback(() => {
    reloadFilters();
    setReloadToken((n) => n + 1);
  }, [reloadFilters]);

  const pageError = filtersResult.error;
  const statusOptions = useMemo(
    () => ICR_STATUSES.map((value) => ({ value, label: ICR_STATUS_LABEL[value] })),
    [],
  );

  /* --- chips ------------------------------------------------------------- */

  const cycleIsCustom = urlCycleId !== null && (filters === null || urlCycleId !== filters.resolvedCycleId);
  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (cycleIsCustom) {
    chips.push({
      key: 'cycle',
      label: `Cycle: ${selectedCycle?.name ?? urlCycleId}`,
      onRemove: () => writeParam('cycle', null),
    });
  }
  if (status !== DEFAULT_ICR_STATUS) {
    chips.push({
      key: 'status',
      label: `Reports: ${ICR_STATUS_LABEL[status]}`,
      onRemove: () => writeParam('status', null),
    });
  }
  if (draftSearch.trim() !== '') {
    chips.push({
      key: 'q',
      label: `Search: ${draftSearch.trim()}`,
      onRemove: () => setDraftSearch(''),
    });
  }

  const facts = (
    <>
      {selectedCycle !== null && (
        <>
          <span className="tabular-nums">
            {formatDateOnly(selectedCycle.startDate)} – {formatDateOnly(selectedCycle.endDate)}
          </span>
        </>
      )}
    </>
  );

  return (
    <PiPageLayout
      product="icr"
      title="In-Country Representation"
      facts={facts}
      actions={
        <>
          <UpdatedFact
            fetchedAt={summary.data?.fetchedAt ?? null}
            refreshing={summary.refreshing}
            onRefresh={refresh}
          />
          <LayoutToolbar layout={layout} />
        </>
      }
    >
      <PiToolbar
        chips={chips.length > 0 ? <PiFilterChips chips={chips} onReset={resetFilters} /> : undefined}
      >
        <div className="w-48 max-sm:w-full">
          <Select
            aria-label="Cycle"
            value={selectedCycleId !== null ? String(selectedCycleId) : ''}
            onChange={onCycleChange}
            disabled={!filters}
          >
            {!filters && <option value="">Cycle</option>}
            {filters?.cycles.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.isCurrent ? `${c.name} (current)` : c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="max-sm:w-full">
          <SegmentedControl label="Reports" value={status} options={statusOptions} onChange={onStatusChange} />
        </div>
        <PiSearch
          value={draftSearch}
          onChange={setDraftSearch}
          label="Search universities"
          className="w-full sm:w-72"
        />
      </PiToolbar>

      {pageError !== null && (
        <PiError
          message={`Couldn’t load the cycle list. ${errorMessage(pageError, 'The SEED platform could not be reached.')}`}
          onRetry={retry}
        />
      )}

      <IcrStats overall={summary.data?.overall ?? null} loading={summary.loading} />

      <LayoutGrid
        layout={layout}
        render={{
          outcomes: () => <IcrOutcomes overall={summary.data?.overall ?? null} loading={summary.loading} />,
          universities: () => (
            <IcrUniversityTable
              rows={summary.data?.rows ?? null}
              overall={summary.data?.overall ?? null}
              search={draftSearch}
              loading={summary.refreshing}
              error={summary.error}
              expanded={expanded}
              onToggle={toggle}
              months={months}
              onClearSearch={draftSearch !== '' ? () => setDraftSearch('') : undefined}
              onRetry={refresh}
            />
          ),
        }}
      />
    </PiPageLayout>
  );
}

export default ProductInsightsIcrPage;
